import { useEffect, useId, useRef, useState, type FormEvent } from "react";

import {
  createCustomer,
  CustomerNumberTakenError,
  customersWithEmail,
  ProblemError,
  searchCustomers,
  type CustomerOut,
} from "@/api";
import { Button } from "@/components/Button";
import { DialogFooter } from "@/components/Dialog";
import { TextField } from "@/components/fields/TextField";
import { StateMessage } from "@/components/StateMessage";

import styles from "./customer.module.css";

/** What the form edits — every field a string, blank until typed. */
export type CustomerDraft = {
  displayName: string;
  company: string;
  email: string;
  phone: string;
  mobile: string;
  street: string;
  postalCode: string;
  city: string;
  customerNo: string;
};

const EMPTY_DRAFT: CustomerDraft = {
  displayName: "",
  company: "",
  email: "",
  phone: "",
  mobile: "",
  street: "",
  postalCode: "",
  city: "",
  customerNo: "",
};

/** The server's own rule, no stricter (`_checked_email`, Plan B): blank, or with an „@". */
const EMAIL_WITHOUT_AT = "Eine E-Mail-Adresse braucht ein „@“ — trag sie vollständig ein oder lass das Feld leer.";

function blankToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

function messageOf(cause: unknown, what: string): string {
  return cause instanceof ProblemError ? cause.userMessage : `${what} — Server nicht erreichbar.`;
}

/** What a stored customer shows in the form: a field they do not have is blank. */
function draftOf(customer: CustomerOut): CustomerDraft {
  return {
    displayName: customer.displayName,
    company: customer.company ?? "",
    email: customer.email ?? "",
    phone: customer.phone ?? "",
    mobile: customer.mobile ?? "",
    street: customer.street ?? "",
    postalCode: customer.postalCode ?? "",
    city: customer.city ?? "",
    customerNo: customer.customerNo ?? "",
  };
}

function sameDraft(a: CustomerDraft, b: CustomerDraft): boolean {
  return (Object.keys(EMPTY_DRAFT) as (keyof CustomerDraft)[]).every((key) => a[key] === b[key]);
}

/** How a customer is called in a sentence: the company where there is one. */
function calledBy(customer: CustomerOut): string {
  return customer.company ?? customer.displayName;
}

/** „A“, „A“ und „B“, „A“, „B“ und „C“. */
function quotedList(customers: CustomerOut[]): string {
  const names = customers.map((customer) => `„${calledBy(customer)}“`);
  if (names.length < 2) return names.join("");
  return `${names.slice(0, -1).join(", ")} und ${names[names.length - 1]}`;
}

interface CustomerFormProps {
  /** What is known beforehand — a typed search term, the fields read out of a mail. */
  initial?: Partial<CustomerDraft>;
  /** Where the customer comes from; `mail` marks one that was read out of a mail (D-6). */
  source?: "manual" | "mail";
  /**
   * Where a value of `initial` stands in the mail, in words („aus der
   * Signatur") — said under its field for as long as the field still holds
   * that value. Once a person changes it, the words would be wrong and go.
   */
  sources?: Partial<Record<keyof CustomerDraft, string>>;
  /**
   * Asked before anything is written or handed on — the owner's own fields
   * beside the form (the Eingang's contact person). `false` holds the press;
   * the owner says why, at its field. Nothing is created that could then not
   * be handed on.
   */
  guard?: () => boolean;
  /**
   * The customer is there — created now, or an existing one the person chose
   * instead. The form stays busy until the returned promise settles, and
   * says so itself when it rejects; the owner handles its own refusals.
   */
  onCreated: (customer: CustomerOut) => void | Promise<void>;
  onCancel: () => void;
  /** A request is in flight, or not any more — the dialog around holds Escape meanwhile. */
  onBusy?: (busy: boolean) => void;
  /** The owner holds the form (a hand-over is running, the Vorgang is gone): the button says no. */
  disabled?: boolean;
  /** Why it is held, for the submit button's description. */
  submitDescribedBy?: string;
  submitLabel?: string;
  cancelLabel?: string;
}

/**
 * The customer form (spec 2026-10-20): name, company, e-mail, telephone and
 * mobile, address and the JTL customer number — the shape of a customer in
 * the shared master (D-3), with the address and the number that JTL needs
 * (D-7).
 *
 * **An address warns; a number refuses.** Several customers may share one
 * e-mail address (a family, a firm with one inbox), so before creating it
 * the form asks who already has it (`customersWithEmail`) and, if somebody
 * does, says so — „Den nehmen" hands that customer on, „Trotzdem anlegen"
 * creates a new one anyway. The JTL customer number is the customer's key and
 * the server refuses a second one (409, `field: "customerNo"`): that is said
 * at the number field, with the same way out.
 *
 * Once created, the customer is kept — and the form is about handing them on,
 * not about the draft any more: when the owner could not take them (the
 * assignment failed), the fields turn read-only and show what was stored, the
 * form says so in one line, and its one action is „Erneut zuordnen". A press
 * hands the same customer on; it writes no second one into the master (with
 * addresses allowed to repeat, nothing else would stop that duplicate) and
 * cannot hand on a customer that no longer matches what the screen shows.
 *
 * Nothing that was typed while a look-up ran is applied to the answer: a
 * result that arrives after the draft changed is dropped, and the next press
 * asks about what stands there now.
 */
export function CustomerForm({
  initial,
  source = "manual",
  sources,
  guard,
  onCreated,
  onCancel,
  onBusy,
  disabled = false,
  submitDescribedBy,
  submitLabel = "Anlegen",
  cancelLabel = "Abbrechen",
}: CustomerFormProps) {
  const id = useId();
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const numberRef = useRef<HTMLInputElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);
  const takeRef = useRef<HTMLButtonElement>(null);
  const [draft, setDraft] = useState<CustomerDraft>(() => ({
    ...EMPTY_DRAFT,
    ...Object.fromEntries(Object.entries(initial ?? {}).filter(([, value]) => typeof value === "string")),
  }));
  // What the form opened with — a source is said only while its value still stands.
  const [opened] = useState(draft);
  const [nameError, setNameError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  // Who has the address already — the warning, until the address changes or a way is taken.
  const [sharing, setSharing] = useState<CustomerOut[]>([]);
  // The number is taken: the sentence for its field, and the holder when the search knows them.
  const [numberTaken, setNumberTaken] = useState<{ sentence: string; holder: CustomerOut | null } | null>(null);
  const [refused, setRefused] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The customer written by this form: from here on the form only hands them on.
  const [created, setCreated] = useState<CustomerOut | null>(null);
  // What stands in the draft now, for an answer that arrives after a press.
  const latest = useRef(draft);
  const mounted = useRef(true);
  // Which control takes the focus once it stands in the page: the way out of
  // a warning or a refusal, or the field that carries it.
  const focusAfter = useRef<"take" | "number" | null>(null);

  useEffect(() => {
    latest.current = draft;
  }, [draft]);

  useEffect(() => {
    mounted.current = true;
    nameRef.current?.focus();
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (focusAfter.current === null) return;
    const target = focusAfter.current === "take" ? takeRef.current : numberRef.current;
    if (target === null) return;
    focusAfter.current = null;
    target.focus();
  }, [sharing, numberTaken]);

  function hold(on: boolean) {
    setBusy(on);
    onBusy?.(on);
  }

  function change(key: keyof CustomerDraft, value: string) {
    if (key === "displayName") setNameError(null);
    if (key === "email") {
      setEmailError(null);
      setSharing([]);
    }
    if (key === "customerNo") setNumberTaken(null);
    setDraft((current) => ({ ...current, [key]: value }));
  }

  /** Who holds a taken number: the search takes the number as a term; `null` when it does not find them. */
  async function holderOf(customerId: string, customerNo: string): Promise<CustomerOut | null> {
    try {
      return (await searchCustomers(customerNo)).find((customer) => customer.id === customerId) ?? null;
    } catch {
      return null;
    }
  }

  async function hand(customer: CustomerOut) {
    if (guard !== undefined && !guard()) return;
    hold(true);
    setRefused(null);
    try {
      await onCreated(customer);
    } catch (cause) {
      // The owner's own failure, whatever it was: not necessarily the server's.
      setRefused(cause instanceof ProblemError ? cause.userMessage : "Kunde nicht übernommen. Versuch es noch einmal.");
    } finally {
      if (mounted.current) {
        hold(false);
        submitRef.current?.focus();
      } else {
        onBusy?.(false);
      }
    }
  }

  /** `skipWarning`: the person has seen who has the address and creates anyway. */
  async function submit(skipWarning = false) {
    if (busy || disabled) return;
    if (created !== null) {
      setRefused(null);
      await hand(created);
      return;
    }
    const asked = draft;
    const nameMissing = draft.displayName.trim() === "";
    const email = draft.email.trim();
    const emailWrong = email !== "" && !email.includes("@");
    setNameError(nameMissing ? "Ein Name ist Pflicht." : null);
    setEmailError(emailWrong ? EMAIL_WITHOUT_AT : null);
    if (nameMissing || emailWrong) {
      (nameMissing ? nameRef : emailRef).current?.focus();
      return;
    }
    if (guard !== undefined && !guard()) return;
    setRefused(null);
    setNumberTaken(null);
    hold(true);
    let customer: CustomerOut;
    try {
      if (!skipWarning && email !== "") {
        let others: CustomerOut[];
        try {
          others = await customersWithEmail(email);
        } catch (cause) {
          setRefused(messageOf(cause, "E-Mail nicht geprüft"));
          hold(false);
          submitRef.current?.focus();
          return;
        }
        if (!sameDraft(latest.current, asked)) {
          // Typed while the look-up ran: its answer is about another draft.
          hold(false);
          return;
        }
        if (others.length > 0) {
          focusAfter.current = "take";
          setSharing(others);
          hold(false);
          return;
        }
      }
      customer = await createCustomer({
        displayName: draft.displayName.trim(),
        company: blankToNull(draft.company),
        email: blankToNull(draft.email),
        phone: blankToNull(draft.phone),
        mobile: blankToNull(draft.mobile),
        street: blankToNull(draft.street),
        postalCode: blankToNull(draft.postalCode),
        city: blankToNull(draft.city),
        customerNo: blankToNull(draft.customerNo),
        note: null,
        source,
      });
    } catch (cause) {
      if (cause instanceof CustomerNumberTakenError) {
        // Held a moment longer while the holder is looked up, so the refusal
        // is said once, with the name.
        const holder = await holderOf(cause.existingCustomerId, asked.customerNo.trim());
        hold(false);
        if (!sameDraft(latest.current, asked)) return;
        focusAfter.current = holder !== null ? "take" : "number";
        // The one way out stands under the number now, not the one under the address.
        setSharing([]);
        setNumberTaken({
          sentence:
            holder !== null ? `Diese Kundennummer gehört schon zu „${calledBy(holder)}“.` : cause.message,
          holder,
        });
      } else {
        hold(false);
        setRefused(messageOf(cause, "Kunde nicht angelegt"));
        submitRef.current?.focus();
      }
      return;
    }
    // Shown as stored (the server may have written the number or the phone in
    // its own form), read-only from here on, and nothing of the draft's warnings stays.
    setCreated(customer);
    setDraft(draftOf(customer));
    setSharing([]);
    setNumberTaken(null);
    await hand(customer);
  }

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void submit();
  }

  function field(key: keyof CustomerDraft, label: string, extra: { type?: string; hint?: string } = {}) {
    const from = sources?.[key];
    const stands = from !== undefined && created === null && draft[key] !== "" && draft[key] === opened[key];
    return (
      <TextField
        id={`${id}-${key}`}
        label={label}
        type={extra.type}
        hint={stands ? from : extra.hint}
        value={draft[key]}
        readOnly={created !== null}
        inputRef={key === "displayName" ? nameRef : key === "email" ? emailRef : key === "customerNo" ? numberRef : undefined}
        error={key === "displayName" ? nameError : key === "email" ? emailError : key === "customerNo" ? numberTaken?.sentence : null}
        onChange={(value) => change(key, value)}
      />
    );
  }

  const held = busy || disabled ? "true" : undefined;
  const shareId = `${id}-share`;
  const share = sharing.length > 0 ? `Diese E-Mail gehört schon zu ${quotedList(sharing)}. Trotzdem neu anlegen?` : null;
  const numberHolder = numberTaken?.holder ?? null;
  const createdId = `${id}-created`;

  return (
    <form className={styles.form} noValidate onSubmit={onSubmit}>
      {field("displayName", "Name (Pflicht)")}
      {field("company", "Firma")}
      {field("email", "E-Mail", { type: "email" })}
      <div className={styles.pair}>
        {field("phone", "Telefon", { type: "tel" })}
        {field("mobile", "Mobil", { type: "tel" })}
      </div>
      {field("street", "Straße")}
      <div className={styles.place}>
        {field("postalCode", "PLZ")}
        {field("city", "Ort")}
      </div>
      {field("customerNo", "Kundennummer", { hint: "Die Nummer aus JTL — leer lassen, wenn es noch keine gibt." })}
      {numberHolder !== null ? (
        <Button
          ref={takeRef}
          className={styles.way}
          aria-disabled={held}
          aria-describedby={`${id}-customerNo-error`}
          onClick={() => void hand(numberHolder)}
        >
          Den nehmen
        </Button>
      ) : null}
      {share !== null ? (
        <div className={styles.share}>
          <StateMessage id={shareId} title={share} strong live={false} />
          <div className={styles.actions}>
            {sharing.map((customer, index) => (
              <Button
                key={customer.id}
                ref={index === 0 ? takeRef : undefined}
                aria-disabled={held}
                aria-describedby={shareId}
                onClick={() => void hand(customer)}
              >
                {sharing.length === 1 ? "Den nehmen" : `„${calledBy(customer)}“ nehmen`}
              </Button>
            ))}
            <Button aria-disabled={held} aria-describedby={shareId} onClick={() => void submit(true)}>
              Trotzdem anlegen
            </Button>
          </div>
        </div>
      ) : null}
      {refused !== null ? <StateMessage tone="conflict" live="alert" title={refused} /> : null}
      {created !== null && !busy ? (
        <StateMessage
          id={createdId}
          strong
          live={false}
          title="Der Kunde ist angelegt, aber noch nicht zugeordnet. Noch einmal versuchen."
        />
      ) : null}
      <DialogFooter>
        <Button aria-disabled={held} onClick={onCancel}>
          {cancelLabel}
        </Button>
        <Button
          ref={submitRef}
          type="submit"
          variant="primary"
          busy={busy}
          aria-disabled={held}
          aria-describedby={[submitDescribedBy, created !== null && !busy ? createdId : undefined].filter(Boolean).join(" ") || undefined}
        >
          {busy ? (created !== null ? "Wird zugeordnet …" : "Wird angelegt …") : created !== null ? "Erneut zuordnen" : submitLabel}
        </Button>
      </DialogFooter>
    </form>
  );
}
